/**
 * Platform Admin API services
 */
import { api } from './api';
import type { ApiResponse } from '@/types';

// Helper to convert snake_case to camelCase
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Transform object keys from snake_case to camelCase
function transformKeys<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item)) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(value);
    }
    return result as T;
  }
  return obj as T;
}

// Wrap API call with snake_case to camelCase transformation
async function transformedGet<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    return {
      ...response,
      data: transformKeys<T>(response.data),
    };
  }
  return response as ApiResponse<T>;
}

// Types
export interface OverviewStats {
  totalMdas: number;
  activeMdas: number;
  totalUsers: number;
  activeUsers: number;
  totalDocuments: number;
  activeWorkflows: number;
}

export interface MDA {
  id: string;
  code: string;
  name: string;
  shortName: string;
  type: 'ministry' | 'department' | 'agency';
  parentMdaId: string | null;
  logoUrl: string | null;
  address: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  settings: string;
  createdAt: string;
  updatedAt: string;
}

export interface MdaStats {
  mdaId: string;
  mdaName: string;
  mdaCode: string;
  userCount: number;
  documentCount: number;
  storageUsedMb: number;
}

export interface DocumentType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  allowedExtensions: string;
  maxFileSizeMb: number;
  requiresApproval: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityClassification {
  id: string;
  level: number;
  code: string;
  name: string;
  description: string | null;
  color: string;
  createdAt: string;
}

export interface DocumentStatus {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string;
  isFinal: boolean;
  allowEdit: boolean;
  createdAt: string;
}

export interface FilingScheme {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  isActive: boolean;
  startsAt: string;
  endsAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Analytics API
export const platformAnalytics = {
  getOverview: () => transformedGet<OverviewStats>('/platform/analytics/overview'),
  getMdaStats: () => transformedGet<MdaStats[]>('/platform/analytics/mdas'),
  getDocumentStats: () => transformedGet<{
    totalDocuments: number;
    documentsByType: { typeId: string; typeName: string; count: number }[];
    documentsByStatus: { statusId: string; statusName: string; count: number }[];
    documentsCreatedToday: number;
    documentsCreatedThisWeek: number;
    documentsCreatedThisMonth: number;
  }>('/platform/analytics/documents'),
  getUserStats: () => transformedGet<{
    totalUsers: number;
    activeUsers: number;
    usersByMda: { mdaId: string; mdaName: string; count: number }[];
    loginsToday: number;
    loginsThisWeek: number;
  }>('/platform/analytics/users'),
  getStorageStats: () => transformedGet<{
    totalStorageUsedMb: number;
    storageByMda: { mdaId: string; mdaName: string; usedMb: number }[];
  }>('/platform/analytics/storage'),
  getActivity: (limit = 50) => transformedGet<unknown[]>('/platform/analytics/activity', { limit }),
  getAuditLogs: (params: {
    page?: number;
    perPage?: number;
    action?: string;
    entityType?: string;
    mdaId?: string;
  }) => transformedGet<{ data: unknown[]; total: number }>('/platform/analytics/audit-logs', params),
};

// Platform Bulk Import types
export interface PlatformBulkImportUserRow {
  mdaCode: string;
  firstName: string;
  lastName: string;
  email: string;
  staffId?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  departmentCode?: string | null;
  securityClearance?: number;
  roleCode?: string | null;
}

export interface PlatformBulkImportInput {
  users: PlatformBulkImportUserRow[];
  generatePasswords?: boolean;
  sendWelcomeEmail?: boolean;
}

export interface BulkImportRowResult {
  row: number;
  success: boolean;
  email: string;
  userId?: string;
  pfileId?: string;
  error?: string;
}

export interface BulkImportResult {
  success: boolean;
  totalProcessed: number;
  successCount: number;
  failureCount: number;
  results: BulkImportRowResult[];
}

// MDAs API
export const platformMdas = {
  list: (params?: { page?: number; perPage?: number; search?: string }) =>
    transformedGet<MDA[]>('/platform/mdas', params),
  get: (id: string) => transformedGet<MDA>(`/platform/mdas/${id}`),
  create: async (data: {
    code: string;
    name: string;
    shortName: string;
    type: 'ministry' | 'department' | 'agency';
    contactEmail?: string;
    address?: string;
  }) => {
    const response = await api.post<unknown>('/platform/mdas', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<MDA>(response.data) };
    }
    return response as ApiResponse<MDA>;
  },
  update: async (id: string, data: Partial<MDA>) => {
    const response = await api.put<unknown>(`/platform/mdas/${id}`, data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<MDA>(response.data) };
    }
    return response as ApiResponse<MDA>;
  },
  delete: (id: string) => api.delete(`/platform/mdas/${id}`),
  getStats: (id: string) => transformedGet<MdaStats>(`/platform/mdas/${id}/stats`),
  getUsers: (id: string) => transformedGet<unknown[]>(`/platform/mdas/${id}/users`),
  bulkImportUsers: async (input: PlatformBulkImportInput): Promise<ApiResponse<BulkImportResult>> => {
    const response = await api.post<unknown>('/platform/mdas/users/bulk-import', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<BulkImportResult>(response.data) };
    }
    return response as ApiResponse<BulkImportResult>;
  },
};

// Config API
export const platformConfig = {
  // Document Types
  getDocumentTypes: () => transformedGet<DocumentType[]>('/platform/config/document-types'),
  createDocumentType: async (data: {
    code: string;
    name: string;
    description?: string;
    icon?: string;
    allowedExtensions?: string[];
    maxFileSizeMb?: number;
    requiresApproval?: boolean;
  }) => {
    const response = await api.post<unknown>('/platform/config/document-types', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentType>(response.data) };
    }
    return response as ApiResponse<DocumentType>;
  },
  updateDocumentType: async (id: string, data: Partial<DocumentType>) => {
    const response = await api.put<unknown>(`/platform/config/document-types/${id}`, data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentType>(response.data) };
    }
    return response as ApiResponse<DocumentType>;
  },
  deleteDocumentType: (id: string) => api.delete(`/platform/config/document-types/${id}`),

  // Security Classifications
  getSecurityClassifications: () =>
    transformedGet<SecurityClassification[]>('/platform/config/security-classifications'),
  createSecurityClassification: async (data: {
    level: number;
    code: string;
    name: string;
    description?: string;
    color?: string;
  }) => {
    const response = await api.post<unknown>('/platform/config/security-classifications', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<SecurityClassification>(response.data) };
    }
    return response as ApiResponse<SecurityClassification>;
  },

  // Document Statuses
  getDocumentStatuses: () => transformedGet<DocumentStatus[]>('/platform/config/document-statuses'),
  createDocumentStatus: async (data: {
    code: string;
    name: string;
    description?: string;
    color?: string;
    isFinal?: boolean;
    allowEdit?: boolean;
  }) => {
    const response = await api.post<unknown>('/platform/config/document-statuses', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentStatus>(response.data) };
    }
    return response as ApiResponse<DocumentStatus>;
  },

  // Filing Schemes
  getFilingSchemes: () => transformedGet<FilingScheme[]>('/platform/config/filing-schemes'),
  createFilingScheme: async (data: {
    code: string;
    name: string;
    description?: string;
  }) => {
    const response = await api.post<unknown>('/platform/config/filing-schemes', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<FilingScheme>(response.data) };
    }
    return response as ApiResponse<FilingScheme>;
  },

  // Workflow Templates
  getWorkflowTemplates: () => transformedGet<WorkflowTemplate[]>('/platform/config/workflow-templates'),
  createWorkflowTemplate: async (data: {
    code: string;
    name: string;
    description?: string;
    category?: string;
  }) => {
    const response = await api.post<unknown>('/platform/config/workflow-templates', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<WorkflowTemplate>(response.data) };
    }
    return response as ApiResponse<WorkflowTemplate>;
  },

  // Announcements
  getAnnouncements: () => transformedGet<Announcement[]>('/platform/config/announcements'),
  createAnnouncement: async (data: {
    title: string;
    content: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    startsAt: string;
    endsAt?: string;
  }) => {
    const response = await api.post<unknown>('/platform/config/announcements', data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Announcement>(response.data) };
    }
    return response as ApiResponse<Announcement>;
  },
  updateAnnouncement: async (id: string, data: Partial<Announcement>) => {
    const response = await api.put<unknown>(`/platform/config/announcements/${id}`, data);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Announcement>(response.data) };
    }
    return response as ApiResponse<Announcement>;
  },
  deleteAnnouncement: (id: string) => api.delete(`/platform/config/announcements/${id}`),
};
